import { useUpdateAdminUserMutation } from '@codezo/graphql';
import { Button, Group, Modal, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useEffect } from 'react';

interface EditUserModalProps {
	opened: boolean;
	onClose: () => void;
	onSuccess: () => void;
	user: {
		id: string;
		firstName?: string;
		lastName?: string;
		authenticationUser?: {
			email?: string | null;
			[key: string]: unknown;
		} | null;
		[key: string]: unknown;
	} | null;
}

export function EditUserModal({ opened, onClose, onSuccess, user }: EditUserModalProps) {
	const form = useForm({
		initialValues: {
			firstName: '',
			lastName: '',
		},
		validate: {
			firstName: (value) => (!value ? 'First name is required' : null),
			lastName: (value) => (!value ? 'Last name is required' : null),
		},
	});

	// Update form when user changes
	useEffect(() => {
		if (user) {
			form.setValues({
				firstName: user.firstName || '',
				lastName: user.lastName || '',
			});
		}
	}, [user, form]);

	const [updateUser, { loading }] = useUpdateAdminUserMutation({
		onCompleted: (data) => {
			if (data.updateAdminUser.success) {
				notifications.show({
					title: 'User Updated',
					message: 'User information has been updated successfully',
					color: 'green',
				});
				onSuccess();
				form.reset();
			} else {
				notifications.show({
					title: 'Update Failed',
					message: data.updateAdminUser.message || 'Failed to update user',
					color: 'red',
				});
			}
		},
		onError: (error) => {
			notifications.show({
				title: 'Update Failed',
				message: error.message || 'An error occurred while updating the user',
				color: 'red',
			});
		},
	});

	const handleSubmit = form.onSubmit((values) => {
		if (!user) return;

		updateUser({
			variables: {
				id: user.id,
				input: {
					firstName: values.firstName,
					lastName: values.lastName,
				},
			},
		});
	});

	return (
		<Modal opened={opened} onClose={onClose} title="Edit User" centered>
			<form onSubmit={handleSubmit}>
				<Stack gap="md">
					{user?.authenticationUser?.email && (
						<TextInput label="Email" value={user.authenticationUser.email} disabled />
					)}
					<TextInput
						label="First Name"
						placeholder="Enter first name"
						{...form.getInputProps('firstName')}
						required
					/>
					<TextInput
						label="Last Name"
						placeholder="Enter last name"
						{...form.getInputProps('lastName')}
						required
					/>
					<Group justify="flex-end" gap="sm">
						<Button variant="default" onClick={onClose}>
							Cancel
						</Button>
						<Button type="submit" loading={loading}>
							Update User
						</Button>
					</Group>
				</Stack>
			</form>
		</Modal>
	);
}
